/**
 * Display-only formatters ported from js/filters/*.filter.js.
 */

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatWeight(kg: number, units: 'KG' | 'LB' = 'KG'): string {
  if (!Number.isFinite(kg)) return '-';
  if (units === 'LB') return `${(kg * 2.20462).toFixed(1)} lb`;
  return `${kg.toFixed(1)} kg`;
}

const LB_PER_KG = 2.20462;

/** Short unit label for inline field suffixes. */
export function unitLabel(units: 'KG' | 'LB'): 'kg' | 'lb' {
  return units === 'LB' ? 'lb' : 'kg';
}

/**
 * Weight is stored canonically in kg everywhere. These convert at the input
 * boundary so a user whose preference is LB can enter and see pounds while the
 * DB keeps kilograms. Display is rounded to 1 decimal to avoid round-trip
 * jitter (e.g. 100 lb → 45.36 kg → 100.0 lb).
 */
export function kgToUnit(kg: number, units: 'KG' | 'LB'): number {
  if (!Number.isFinite(kg)) return 0;
  const v = units === 'LB' ? kg * LB_PER_KG : kg;
  return Math.round(v * 10) / 10;
}

/** Inverse of {@link kgToUnit}: a value typed in the user's unit back to kg. */
export function unitToKg(value: number, units: 'KG' | 'LB'): number {
  if (!Number.isFinite(value)) return 0;
  const kg = units === 'LB' ? value / LB_PER_KG : value;
  return Math.round(kg * 100) / 100;
}

const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const DIVS: [number, Intl.RelativeTimeFormatUnit][] = [
  [60, 'second'],
  [60, 'minute'],
  [24, 'hour'],
  [7, 'day'],
  [4.345, 'week'],
  [12, 'month'],
  [Number.POSITIVE_INFINITY, 'year'],
];

export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  let diff = (then - Date.now()) / 1000;
  for (const [div, unit] of DIVS) {
    if (Math.abs(diff) < div) return RTF.format(Math.round(diff), unit);
    diff /= div;
  }
  return RTF.format(Math.round(diff), 'year');
}

const MUSCLE_LABELS: Record<string, string> = {
  chest: 'Chest',
  back: 'Back',
  shoulders: 'Shoulders',
  arms: 'Arms',
  legs: 'Legs',
  core: 'Core',
  cardio: 'Cardio',
  other: 'Other',
};

export function muscleLabel(group: string): string {
  return MUSCLE_LABELS[group] ?? group;
}

const SUBMUSCLE_LABELS: Record<string, string> = {
  upper_chest: 'Upper Chest',
  mid_chest: 'Mid Chest',
  lower_chest: 'Lower Chest',
  lats: 'Lats',
  upper_back: 'Upper Back',
  lower_back: 'Lower Back',
  traps: 'Traps',
  front_delts: 'Front Delts',
  side_delts: 'Side Delts',
  rear_delts: 'Rear Delts',
  short_head: 'Short Head',
  long_head: 'Long Head',
  brachialis: 'Brachialis',
  long_head_triceps: 'Long Head (Triceps)',
  lateral_head: 'Lateral Head',
  medial_head: 'Medial Head',
  forearms: 'Forearms',
  quads: 'Quads',
  vastus_medialis: 'Vastus Medialis',
  hamstrings: 'Hamstrings',
  glutes: 'Glutes',
  gastrocnemius: 'Gastrocnemius',
  soleus: 'Soleus',
  adductors: 'Adductors',
  upper_abs: 'Upper Abs',
  lower_abs: 'Lower Abs',
  obliques: 'Obliques',
  cardio: 'Cardio',
};

export function subMuscleLabel(value: string): string {
  return SUBMUSCLE_LABELS[value] ?? value;
}
